import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { assetUrl } from "@/lib/api";
import { Clock, ChevronDown, ShieldAlert, Plane, BookOpen, ArrowRight, AlertTriangle, RefreshCw } from "lucide-react";

// Image if a verified upload exists, otherwise a polished icon tile (no stock photos).
export function ImageOrIcon({ url, Icon, className = "", rounded = "rounded-2xl" }) {
  const full = assetUrl(url);
  if (full) {
    return <img src={full} alt="" className={`object-cover ${rounded} ${className}`} />;
  }
  return (
    <div className={`att-glass grid place-items-center ${rounded} ${className}`} aria-hidden="true">
      {Icon && <Icon className="h-7 w-7 text-[hsl(var(--pc-gold))]" strokeWidth={1.6} />}
    </div>
  );
}

export function TodayHours({ today, compact = false }) {
  if (!today) return null;
  const tone = today.closed ? "text-[hsl(var(--pc-cream)/0.5)]" : "text-[hsl(var(--pc-gold))]";
  return (
    <span className={`inline-flex items-center gap-1.5 ${tone} ${compact ? "text-xs" : "text-sm"}`} data-testid="explore-today-hours">
      <Clock className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
      <span className="font-medium">{today.summary}</span>
    </span>
  );
}

export function WeeklyHours({ weekly }) {
  const [open, setOpen] = useState(false);
  if (!weekly || weekly.length === 0) return null;
  return (
    <div className="att-card overflow-hidden" data-testid="explore-weekly-hours">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="explore-weekly-toggle"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 font-semibold text-cream">
          <Clock className="h-4 w-4 text-[hsl(var(--pc-gold))]" /> Full weekly hours
        </span>
        <ChevronDown className={`h-4 w-4 text-[hsl(var(--pc-gold))] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-[hsl(var(--pc-line))]">
          {weekly.map((d) => (
            <div key={d.day_key} className="flex items-start justify-between gap-3 py-1.5 text-sm">
              <span className="text-cream-dim w-20 shrink-0">{d.day_label}</span>
              <span className={`text-right ${d.closed ? "text-[hsl(var(--pc-cream)/0.45)]" : "text-cream"}`}>{d.summary}</span>
            </div>
          ))}
          <p className="text-[11px] text-cream-dim mt-2">Hotel local time (Los Cabos).</p>
        </div>
      )}
    </div>
  );
}

export function RestrictionBadges({ restrictions }) {
  if (!restrictions || restrictions.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="explore-restrictions">
      {restrictions.map((r, i) => (
        <span
          key={i}
          title={r.detail || r.label}
          className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--pc-gold)/0.14)] border border-[hsl(var(--pc-gold)/0.4)] px-2.5 py-0.5 text-[11px] font-semibold text-[hsl(var(--pc-gold))]"
        >
          <ShieldAlert className="h-3 w-3" /> {r.label}
        </span>
      ))}
    </div>
  );
}

// Labelled field that hides itself when the value is empty (no "N/A"/undefined).
export function Field({ label, value, children }) {
  const content = children ?? value;
  if (content === null || content === undefined || content === "") return null;
  return (
    <div className="py-2 border-b border-[hsl(var(--pc-line))] last:border-0" data-testid={`explore-field-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-cream-dim">{label}</div>
      <div className="text-sm text-cream mt-0.5 leading-relaxed whitespace-pre-line">{content}</div>
    </div>
  );
}

export function ExploreHeader({ eyebrow, title, subtitle, icon: Icon }) {
  return (
    <div className="pt-4 pb-3">
      {eyebrow && <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">{eyebrow}</span>}
      <div className="flex items-start gap-2.5">
        {Icon && (
          <span className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]">
            <Icon className="h-4.5 w-4.5" strokeWidth={1.7} />
          </span>
        )}
        <h1 className="font-serif text-4xl text-cream leading-tight">{title}</h1>
      </div>
      {subtitle && <p className="text-sm text-cream-dim mt-1">{subtitle}</p>}
    </div>
  );
}

// Secondary navigation link used on Explore, holding and error screens.
export function ExploreSecondaryLink({ icon: Icon, title, subtitle, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full att-card p-3.5 flex items-center justify-between focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] outline-none">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]"><Icon className="h-5 w-5" /></div>
        <div className="text-left">
          <div className="font-semibold text-cream">{title}</div>
          <div className="text-xs text-cream-dim">{subtitle}</div>
        </div>
      </div>
      <ArrowRight className="h-5 w-5 text-[hsl(var(--pc-gold))]" />
    </button>
  );
}

function ExploreTripLinks() {
  const navigate = useNavigate();
  return (
    <div className="mt-5 space-y-2">
      <ExploreSecondaryLink icon={Plane} title="My Trip" subtitle="Your flights & personal transfers" onClick={() => navigate("/app/trip")} testid="explore-link-mytrip" />
      <ExploreSecondaryLink icon={BookOpen} title="Know Before You Go" subtitle="Travel, entry & guest guidance" onClick={() => navigate("/app/more?tab=kbyg")} testid="explore-link-kbyg" />
    </div>
  );
}

// Holding screen — the safe default when Explore is not yet activated/configured.
export function ExploreHolding() {
  return (
    <div className="pt-16 px-4 pb-6">
      <div className="pt-4 pb-3">
        <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Discover</span>
        <h1 className="font-serif text-4xl text-cream leading-tight">Explore</h1>
      </div>
      <div className="att-card p-6 text-center" data-testid="explore-holding">
        <div className="font-serif text-lg text-cream">Explore is being updated</div>
        <p className="text-sm text-cream-dim mt-1">Your resort guide will be available here shortly. In the meantime:</p>
      </div>
      <ExploreTripLinks />
    </div>
  );
}

// Error screen — shown on API/auth failure. NEVER rendered as "empty results".
export function ExploreError({ onRetry }) {
  return (
    <div className="pt-16 px-4 pb-6">
      <div className="pt-4 pb-3">
        <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Discover</span>
        <h1 className="font-serif text-4xl text-cream leading-tight">Explore</h1>
      </div>
      <div className="att-card p-6 text-center" data-testid="explore-error">
        <AlertTriangle className="h-6 w-6 text-[hsl(var(--pc-gold))] mx-auto mb-2" />
        <div className="font-serif text-lg text-cream">We couldn't load Explore</div>
        <p className="text-sm text-cream-dim mt-1">Please check your connection and try again.</p>
        {onRetry && (
          <button onClick={onRetry} data-testid="explore-retry" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--pc-gold))] text-[hsl(var(--pc-ink))] px-4 py-1.5 text-xs font-semibold">
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </button>
        )}
      </div>
      <ExploreTripLinks />
    </div>
  );
}
